import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  type EscalateResult,
  FORCEABLE_CATEGORIES,
  firesImmediately,
  forceEscalate,
  ISSUE_REASON_MAX_CHARS,
} from '../lib/issue-actions';
import { createMutation } from '../lib/mutation';
import { Field } from './Field';
import { Select } from './Select';
import { buttonClass } from './ui/Button';

// Force an escalation on one issue (arc 101 slice 06). Presenter + its own
// submit: category × reason → `POST /v1/issues/:id/escalate`.
//
// Two truths the form refuses to hide. Before the submit: a hardcoded-always
// category (security / license / cla) emails a human the moment it lands, so
// say so while the button is still unpressed. After it: the route answers
// `deduped` when an open escalation for the same (issue, category) already
// existed — nothing new fired, nothing re-notified, and the result line says
// exactly that rather than a triumphant "recorded".

/** Keyed category label when the catalog has one, else the raw domain value. */
function categoryLabel(category: string): string {
  const key = `repo_detail.issue_cat_${category}`;
  return t.has(key) ? t(key) : category;
}

export function IssueEscalateForm(props: {
  issueId: string;
  token: string | null;
  /** Fired after a 202 so the parent can re-read server truth. */
  onActed: () => void;
}): JSX.Element {
  const [category, setCategory] = createSignal<string>(FORCEABLE_CATEGORIES[0] ?? 'security');
  const [reason, setReason] = createSignal('');
  const [result, setResult] = createSignal<EscalateResult | null>(null);

  const valid = (): boolean => reason().trim().length > 0;

  const escalate = createMutation({
    call: (input: { category: string; reason: string }) => {
      // Cleared HERE rather than in `submit`, so a click the kit drops as
      // re-entrant leaves the previous verdict on screen instead of blanking it
      // over a write that never started.
      setResult(null);
      return forceEscalate(props.issueId, input, props.token);
    },
    fallback: t('repo_detail.issue_escalate_error'),
    onSuccess: (out) => {
      setResult(out);
      setReason('');
      props.onActed();
    },
  });
  const busy = escalate.pending;
  const error = escalate.error;

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!valid()) return;
    void escalate.run({ category: category(), reason: reason().trim() });
  };

  return (
    <form class="issue-action" aria-label={t('repo_detail.issue_escalate_title')} onSubmit={submit}>
      <h3 class="issue-action__title">{t('repo_detail.issue_escalate_title')}</h3>
      <p class="issue-action__desc">{t('repo_detail.issue_escalate_desc')}</p>

      <Field label={t('repo_detail.issue_escalate_category')} id="issue-escalate-category">
        {(a11y) => (
          <>
            <Select
              a11y={a11y}
              label={t('repo_detail.issue_escalate_category')}
              value={category()}
              disabled={busy()}
              options={FORCEABLE_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(c) }))}
              onChange={setCategory}
            />
            {/* A warning, not a hint: it keeps its own tone class, so it is a
                sibling of the control rather than the field's `desc`. */}
            <Show when={firesImmediately(category())}>
              <p class="field__desc issue-action__warn">
                {t('repo_detail.issue_escalate_immediate')}
              </p>
            </Show>
          </>
        )}
      </Field>

      <Field
        label={t('repo_detail.issue_reason')}
        id="issue-escalate-reason"
        desc={t('repo_detail.issue_reason_desc')}
      >
        {(a11y) => (
          <textarea
            {...a11y}
            class="textarea"
            rows={3}
            maxlength={ISSUE_REASON_MAX_CHARS}
            disabled={busy()}
            placeholder={t('repo_detail.issue_escalate_reason_placeholder')}
            value={reason()}
            onInput={(e) => setReason(e.currentTarget.value)}
          />
        )}
      </Field>

      <Show when={result()}>
        {(r) => (
          <p
            class="issue-action__result"
            classList={{ 'issue-action__warn': r().deduped === true }}
            role="status"
          >
            {r().deduped
              ? t('repo_detail.issue_escalate_deduped', { category: categoryLabel(category()) })
              : t(
                  r().fires_immediately
                    ? 'repo_detail.issue_escalate_done_email'
                    : 'repo_detail.issue_escalate_done',
                )}
          </p>
        )}
      </Show>
      <Show when={error()}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>

      <button type="submit" class={buttonClass({ size: 'sm' })} disabled={!valid() || busy()}>
        {t(busy() ? 'repo_detail.issue_escalate_busy' : 'repo_detail.issue_escalate_submit')}
      </button>
    </form>
  );
}
